import React, { useEffect } from 'react';
import { format } from 'date-fns';
import { formatCompanyDate } from '../../utils/companyDateFormat';
import { X } from 'lucide-react';
import { Shift, ShiftMarketplaceRequest } from '../../types';
import { MarketplaceCoverBadge } from './MarketplaceCoverDetailsPopup';

export type MonthShiftRow = {
  shift: Shift;
  employeeName: string;
  startLabel: string;
  endLabel: string;
  color: string;
  marketplaceBadge?: string;
  marketplaceRequest?: ShiftMarketplaceRequest;
};

export function SchedulingDayShiftsModal({
  date,
  rows,
  onClose,
}: {
  date: Date;
  rows: MonthShiftRow[];
  onClose: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="day-shifts-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
          <h2 id="day-shifts-modal-title" className="text-lg font-semibold text-gray-900">
            {format(date, 'EEEE')}, {formatCompanyDate(date)}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-4 py-2 text-sm text-gray-500 border-b flex-shrink-0">
          {rows.length} {rows.length === 1 ? 'shift' : 'shifts'}
        </div>
        <div className="overflow-y-auto flex-1 min-h-0 p-2">
          {rows.length === 0 ? (
            <div className="px-3 py-8 text-center text-gray-500 text-sm">No shifts for this day.</div>
          ) : (
            <ul className="space-y-1">
              {rows.map((row) => (
                <li
                  key={row.shift.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-md border border-gray-100"
                  style={{ borderLeftWidth: 3, borderLeftColor: row.color || '#3b82f6' }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">{row.employeeName}</div>
                    <div className="text-xs text-gray-600">
                      {row.startLabel} – {row.endLabel}
                    </div>
                    {row.marketplaceRequest && (
                      <MarketplaceCoverBadge request={row.marketplaceRequest} variant="onLight" />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="px-4 py-3 border-t flex justify-end flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
